import { useCurrency } from "@/lib/currency";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useSuspenseQuery, useMutation, useQueryClient, queryOptions } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Suspense } from "react";

import {
  listCustomTours,
  deleteCustomTour,
  toggleActiveCustomTour,
} from "@/lib/admin-tours.functions";
import { formatPrice } from "@/lib/slug";
import { SiteHeader } from "@/components/site/SiteHeader";

const toursQuery = queryOptions({
  queryKey: ["admin", "tours"],
  queryFn: () => listCustomTours(),
});

export const Route = createFileRoute("/_authenticated/admin/tours/")({
  head: () => ({
    meta: [
      { title: "Admin · Tours — Mostrando México" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminToursPage,
});

function AdminToursPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl font-semibold">Tours propios</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Catálogo de Mostrando México. Conviven con productos Bokun en el sitio público.
            </p>
          </div>
          <Link
            to="/admin/tours/new"
            className="inline-flex h-11 items-center rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground"
          >
            + Nuevo tour
          </Link>
        </div>

        <Suspense fallback={<div className="mt-8 text-muted-foreground">Cargando…</div>}>
          <ToursTable />
        </Suspense>
      </div>
    </div>
  );
}

function ToursTable() {
  useCurrency(); // subscribe: formatPrice reads the currency store
  const { data } = useSuspenseQuery(toursQuery);
  const qc = useQueryClient();
  const router = useRouter();

  const del = useServerFn(deleteCustomTour);
  const toggle = useServerFn(toggleActiveCustomTour);

  const delMut = useMutation({
    mutationFn: del,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "tours"] });
      qc.invalidateQueries({ queryKey: ["catalog"] });
    },
  });
  const toggleMut = useMutation({
    mutationFn: toggle,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "tours"] });
      qc.invalidateQueries({ queryKey: ["catalog"] });
    },
  });

  if (data.tours.length === 0) {
    return (
      <div className="mt-10 rounded-2xl border border-dashed border-border bg-card p-12 text-center">
        <p className="font-display text-xl">Aún no hay tours propios</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Crea tu primer tour para empezar a vender productos sin depender de Bokun.
        </p>
        <Link
          to="/admin/tours/new"
          className="mt-6 inline-flex rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground"
        >
          Crear primer tour
        </Link>
      </div>
    );
  }

  return (
    <div className="mt-8 overflow-hidden rounded-2xl border border-border bg-card">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left">
          <tr>
            <th className="px-4 py-3">Tour</th>
            <th className="px-4 py-3">Ubicación</th>
            <th className="px-4 py-3">Precio</th>
            <th className="px-4 py-3">Estado</th>
            <th className="px-4 py-3 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {data.tours.map((t: any) => (
            <tr key={t.id} className="border-t border-border">
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  {t.image ? (
                    <img loading="lazy" decoding="async" src={t.image} alt="" className="h-10 w-14 rounded-md object-cover" />
                  ) : (
                    <div className="h-10 w-14 rounded-md bg-muted" />
                  )}
                  <div>
                    <div className="font-semibold">{t.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {t.featured && <span className="mr-1 text-primary">★ Destacado</span>}
                      <span>/tour/mx-{t.slug}</span>
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 text-muted-foreground">
                {[t.city, t.state].filter(Boolean).join(", ") || "—"}
              </td>
              <td className="px-4 py-3">{formatPrice(t.priceFrom, t.currency)}</td>
              <td className="px-4 py-3">
                <button
                  type="button"
                  onClick={() =>
                    toggleMut.mutate({ data: { id: t.id, active: !t.active } })
                  }
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    t.active
                      ? "bg-success/15 text-success"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {t.active ? "Activo" : "Inactivo"}
                </button>
              </td>
              <td className="px-4 py-3 text-right">
                <Link
                  to="/admin/tours/$id/inventory"
                  params={{ id: t.id }}
                  className="mr-3 text-primary hover:underline"
                >
                  Inventario
                </Link>
                <Link
                  to="/admin/tours/$id/edit"
                  params={{ id: t.id }}
                  className="mr-3 text-primary hover:underline"
                >
                  Editar
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    if (confirm(`¿Eliminar "${t.title}"?`)) {
                      delMut.mutate({ data: { id: t.id } });
                    }
                  }}
                  className="text-destructive hover:underline"
                >
                  Eliminar
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
